import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import Navigation from '../Navigation';
import * as ROUTES from '../../constants/routes';
import { AuthProvider } from '../Session';
import { queryClient } from '../../queryClient';

// Lazy load heavy pages for better initial load performance (Phase 1)
const HomePage = lazy(() => import('../HomePage'));
const PrivacyPage = lazy(() => import('../Privacy'));
const TermsConditionsPage = lazy(() => import('../Terms'));
const SignInPage = lazy(() => import('../SignIn'));
const SignUpPage = lazy(() => import('../SignUp'));
const PasswordForgetPage = lazy(() => import('../PasswordForget'));
const AccountPage = lazy(() => import('../Account'));
const ShareCoinsPage = lazy(() => import('../Account/ShareCoinsPage'));
const VisitorsPage = lazy(() => import('../Account/VisitorsPage'));
const AdminPage = lazy(() => import('../Admin'));
const AddMintPage = lazy(() => import('../Admin/AddMintPage'));
const CoinPricingPage = lazy(() => import('../Admin/CoinPricingPage'));
const AdminUserItemPage = lazy(() => import('../Admin/AdminUserItemPage'));
const AdminUserEditPage = lazy(() => import('../Admin/AdminUserEditPage'));
const UserPage = lazy(() => import('../User'));
const InvestorPage = lazy(() => import('../Investor'));
const BankerPage = lazy(() => import('../Banker'));
const DashboardPage = lazy(() => import('../Dashboard'));

const CoinsPage = lazy(() => import('../Coins'));
const CoinsCreatePage = lazy(() => import('../Coins/CoinsCreatePage'));
const CoinsEditPage = lazy(() => import('../Coins/CoinsEditPage'));
const CoinsCasePage = lazy(() => import('../Coins/CoinsCasePage'));
const CoinsCaseDetails = lazy(() => import('../Coins/CoinsCaseDetails'));
const CoinsAddPage = lazy(() => import('../Coins/CoinsAddPage'));
const CoinsPurchasePage = lazy(() => import('../Coins/CoinsPurchasePage'));
const CoinsPurchaseSuccessPage = lazy(() => import('../Coins/CoinsPurchaseSuccessPage'));
const CoinsTransferPage = lazy(() => import('../Coins/CoinsTransferPage'));

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Router>
      <AuthProvider>
        <Navigation />
        <Suspense fallback={<div className="p-5 text-center">Loading...</div>}>
          <Routes>
            <Route path={ROUTES.HOME} element={<HomePage />} />
            <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
            <Route path={ROUTES.TERMS} element={<TermsConditionsPage />} />
            <Route path={ROUTES.SIGN_UP} element={<SignUpPage />} />
            <Route path={ROUTES.SIGN_IN} element={<SignInPage />} />
            <Route path={ROUTES.PASSWORD_FORGET} element={<PasswordForgetPage />} />
            <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
            <Route path={ROUTES.COINS} element={<CoinsPage />} />
            <Route path={ROUTES.COINS_CREATE} element={<CoinsCreatePage />} />
            <Route path={ROUTES.COINS_CASE} element={<CoinsCasePage />} />
            <Route path={ROUTES.COINS_CASE_DETAILS} element={<CoinsCaseDetails />} />
            <Route path={ROUTES.COINS_CASE_TRANSFER} element={<CoinsTransferPage />} />
            <Route path={ROUTES.COINS_CASE_ADD} element={<CoinsAddPage />} />
            <Route
              path={ROUTES.COINS_CASE_PURCHASE_SUCCESS}
              element={<CoinsPurchaseSuccessPage />}
            />
            <Route path={ROUTES.COINS_CASE_PURCHASE} element={<CoinsPurchasePage />} />
            <Route path={ROUTES.COINS_CASE_EDIT} element={<CoinsEditPage />} />
            <Route path={ROUTES.USER} element={<UserPage />} />
            <Route path={ROUTES.INVESTOR} element={<InvestorPage />} />
            <Route path={ROUTES.BANKER} element={<BankerPage />} />
            <Route path={ROUTES.ADMIN_ADD_MINT} element={<AddMintPage />} />
            <Route path={ROUTES.ADMIN_COIN_PRICING} element={<CoinPricingPage />} />
            <Route
              path={ROUTES.ADMIN_MONARCH_PRICING}
              element={<Navigate to={ROUTES.ADMIN_COIN_PRICING} replace />}
            />
            <Route path={ROUTES.ADMIN_EDIT_USER} element={<AdminUserEditPage />} />
            <Route path={ROUTES.ADMIN_DETAILS} element={<AdminUserItemPage />} />
            <Route path={ROUTES.ADMIN} element={<AdminPage />} />
            <Route path={ROUTES.ACCOUNT} element={<AccountPage />} />
            <Route path={ROUTES.ACCOUNT_SHARE} element={<ShareCoinsPage />} />
            <Route path={ROUTES.ACCOUNT_VISITORS} element={<VisitorsPage />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </Router>
  </QueryClientProvider>
);

export default App;
